import { useState } from "react";
import { Icon } from "../../design-system/Icon";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import { strings } from "../../i18n/es-CL";
import type { NotificationsList, NoticeLevel } from "../../ipc/types";

const LEVEL_TONE: Record<NoticeLevel, "danger" | "warning" | "brand"> = {
  error: "danger", warning: "warning", info: "brand",
};
const LEVEL_ICON: Record<NoticeLevel, "alert" | "bell"> = {
  error: "alert", warning: "alert", info: "bell",
};

const fmtWhen = (ms: number): string => {
  const diff = Date.now() - ms;
  const min = Math.round(diff / 60000);
  if (min < 1) return "recién";
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  return new Date(ms).toLocaleDateString("es-CL");
};

/**
 * Centro de avisos (item 5): la campana del Topbar muestra los fallos de fondo (respaldo
 * automático, sincronización con Mercado Libre) que antes sólo iban al log. Badge con no leídos
 * y un panel desplegable para revisarlos, marcarlos leídos y descartarlos.
 */
export function NotificationsCenter(): JSX.Element {
  const client = useIpc();
  const [open, setOpen] = useState(false);
  const { state, reload } = useIpcQuery<NotificationsList>((c) => c.listNotifications(), []);
  const data = state.status === "ready" ? state.data : { items: [], unread: 0 };

  const toggle = async (): Promise<void> => {
    const next = !open;
    setOpen(next);
    if (next) reload();
  };
  const markAllRead = async (): Promise<void> => { await client.markAllNotificationsRead(); reload(); };
  const dismiss = async (id: string): Promise<void> => { await client.dismissNotification(id); reload(); };
  const clearAll = async (): Promise<void> => { await client.clearNotifications(); reload(); };

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        className={`iconbtn${data.unread > 0 ? " iconbtn--badge" : ""}`}
        aria-label={`${strings.notifications}${data.unread > 0 ? ` (${data.unread} sin leer)` : ""}`}
        aria-expanded={open}
        onClick={toggle}
      >
        <Icon name="bell" size={18} />
        {data.unread > 0 ? (
          <span aria-hidden style={{ position: "absolute", top: 4, right: 4, minWidth: 16, height: 16, padding: "0 4px", borderRadius: 999, background: "var(--danger)", color: "#fff", fontSize: 10, fontWeight: 700, lineHeight: "16px", textAlign: "center" }}>
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          {/* Capa para cerrar al hacer clic fuera. */}
          <div role="presentation" onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 90 }} />
          <div
            role="dialog" aria-label="Centro de avisos"
            style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, width: 360, maxHeight: 460, overflowY: "auto", background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg, 14px)", boxShadow: "0 20px 60px rgba(0,0,0,0.35)", zIndex: 100 }}
          >
            <div className="row" style={{ justifyContent: "space-between", alignItems: "center", padding: "var(--sp-4)", borderBottom: "1px solid var(--border)" }}>
              <h3 className="strong" style={{ fontSize: 14 }}>Avisos</h3>
              <div className="row" style={{ gap: 6 }}>
                <Button variant="ghost" size="sm" disabled={data.items.length === 0} onClick={markAllRead}>Marcar leídas</Button>
                <Button variant="ghost" size="sm" disabled={data.items.length === 0} onClick={clearAll}>Limpiar</Button>
              </div>
            </div>
            {data.items.length === 0 ? (
              <p className="muted" style={{ fontSize: 13, padding: "var(--sp-5)", textAlign: "center", margin: 0 }}>No hay avisos. Aquí aparecerán los fallos de fondo (respaldo, sincronización).</p>
            ) : (
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {data.items.map((n) => (
                  <li key={n.id} style={{ display: "flex", gap: 10, padding: "var(--sp-3) var(--sp-4)", borderBottom: "1px solid var(--border)", background: n.read ? "transparent" : "color-mix(in srgb, var(--brand) 6%, transparent)" }}>
                    <Icon name={LEVEL_ICON[n.level]} size={16} style={{ marginTop: 2, color: `var(--${LEVEL_TONE[n.level] === "danger" ? "danger" : LEVEL_TONE[n.level] === "warning" ? "warning" : "brand"})` }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="row" style={{ justifyContent: "space-between", gap: 8 }}>
                        <span className="strong" style={{ fontSize: 13 }}>{n.title}</span>
                        <Badge tone={LEVEL_TONE[n.level]} dot>{n.level === "error" ? "Error" : n.level === "warning" ? "Aviso" : "Info"}</Badge>
                      </div>
                      {n.message ? <p className="muted" style={{ fontSize: 12.5, margin: "2px 0 4px", lineHeight: 1.45 }}>{n.message}</p> : null}
                      <div className="row" style={{ justifyContent: "space-between", gap: 8 }}>
                        <span className="faint" style={{ fontSize: 11.5 }}>{fmtWhen(n.at)}</span>
                        <button type="button" className="linklike" style={{ fontSize: 11.5, background: "none", border: "none", color: "var(--muted)", cursor: "pointer", padding: 0 }} onClick={() => dismiss(n.id)}>Descartar</button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
